import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { paymentsShape, customerShape, passengersShape, journeyShape, compensationsShape } from 'constants/propTypes';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { useHistory } from 'react-router';

import { getBookingJourneysURL } from 'utils/urls';
import { codes as paymentCodes } from 'constants/payment';
import BackLink from 'components/ui/BackLink';
import { Page, Row } from 'components/shared/Layout';
import Tabs from 'components/ui/Tabs';
import PrrRefundCard from './PrrRefundCard';
import PrrRefundLoyalty from './PrrRefundLoyalty';
import PrrRefundVoucher from './PrrRefundVoucher';
import {
  LOYALTY_TAB_VALUE,
  CARD_TAB_VALUE,
  BAF_TAB_VALUE,
  VOUCHER_TAB_VALUE,
  loyaltyTab,
  cardTab,
  voucherTab,
  bafTab
} from './PrrRefund.utils';
import PrrRefundBAF from './PrrRefundBAF';

const PrrRefund = ({
  onSubmit,
  reference,
  filteredProducts,
  compensateLegs,
  compensationItems,
  payments,
  passengers,
  customer,
  loading,
  itemRefs,
  additionalDetails,
  currency,
  legs,
  username
}) => {
  const history = useHistory();
  const location = useLocation();
  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const isLoyaltyBooking = filteredProducts?.some((pf) => pf?.productFamily?.familyGroup === productFamilyCodes.EUROSTAR_LOYALTY);
  const canRefundInLoyaltyPoint =
    !isLoyaltyBooking &&
    compensationItems?.length > 0 &&
    compensationItems.every(
      ({ isEligiblePoints, membershipNumber: compensationMembershipNumber }) =>
        isEligiblePoints === true && Boolean(compensationMembershipNumber)
    );

  const tabs = [voucherTab];

  const cardPayments = payments.filter(
    ({ method }) =>
      method === paymentCodes.CARD ||
      method === paymentCodes.APPLE_PAY ||
      method === paymentCodes.GOOGLE_PAY ||
      method === paymentCodes.IDEAL ||
      method === paymentCodes.PAYPAL ||
      method === paymentCodes.KLARNA_BNPL
  );
  const bafPayments = payments.filter(({ method }) => method === paymentCodes.BUSINESS_ACCOUNT);

  if (isLoyaltyBooking) {
    tabs.unshift(loyaltyTab);
  } else {
    if (canRefundInLoyaltyPoint) {
      tabs.unshift(loyaltyTab);
    }
    if (cardPayments.length > 0) {
      tabs.unshift(cardTab);
    }
    if (bafPayments.length > 0) {
      tabs.unshift(bafTab);
    }
  }

  const hash = location?.hash?.substring(1) || tabs[0].value;
  const [activeTab, setActiveTab] = useState(hash);

  const renderTabs = {
    [VOUCHER_TAB_VALUE]: (
      <PrrRefundVoucher
        itemRefs={itemRefs}
        reference={reference}
        filteredProducts={filteredProducts}
        compensateLegs={compensateLegs}
        compensationItems={compensationItems}
        passengers={passengers}
        customer={customer}
        loading={loading}
        onSubmit={onSubmit}
        currency={currency}
        legs={legs}
      />
    ),
    [CARD_TAB_VALUE]: (
      <PrrRefundCard
        itemRefs={itemRefs}
        reference={reference}
        filteredProducts={filteredProducts}
        compensateLegs={compensateLegs}
        compensationItems={compensationItems}
        payments={cardPayments}
        loading={loading}
        onSubmit={onSubmit}
        currency={currency}
      />
    ),
    [LOYALTY_TAB_VALUE]: (
      <PrrRefundLoyalty
        username={username}
        itemRefs={itemRefs}
        reference={reference}
        filteredProducts={filteredProducts}
        compensateLegs={compensateLegs}
        compensationItems={compensationItems}
        isCashTicketRefund={canRefundInLoyaltyPoint}
        loading={loading}
        onSubmit={onSubmit}
        membershipNumber={membershipNumber}
        additionalDetails={additionalDetails}
        currency={currency}
        isLoyaltyBooking={isLoyaltyBooking}
      />
    ),
    [BAF_TAB_VALUE]: (
      <PrrRefundBAF
        itemRefs={itemRefs}
        reference={reference}
        filteredProducts={filteredProducts}
        compensateLegs={compensateLegs}
        compensationItems={compensationItems}
        payments={bafPayments}
        loading={loading}
        onSubmit={onSubmit}
        currency={currency}
      />
    )
  };

  return (
    <Page data-testid="prr-refund">
      <Row span={14} start={2}>
        <BackLink to={getBookingJourneysURL(reference, membershipNumber)}>
          <FormattedMessage id="prrRefund.tickets" />
        </BackLink>
        <h1>
          <FormattedMessage id="prrRefund.prrRefund" />
        </h1>
        <Tabs tabs={tabs} onClick={setActiveTab} selectedValue={activeTab} />
        {renderTabs[activeTab]}
      </Row>
    </Page>
  );
};

PrrRefund.propTypes = {
  filteredProducts: PropTypes.arrayOf(
    PropTypes.shape({
      itemRef: PropTypes.string,
      passengerType: PropTypes.string,
      bucketCode: PropTypes.string,
      productFamily: PropTypes.shape({
        code: PropTypes.string
      }),
      currency: PropTypes.string,
      loyaltyPoints: PropTypes.number,
      price: PropTypes.number
    })
  ),
  compensateLegs: PropTypes.arrayOf(
    PropTypes.shape({
      legName: PropTypes.string,
      products: PropTypes.arrayOf(
        PropTypes.shape({
          itemRef: PropTypes.string,
          passengerType: PropTypes.string,
          bucketCode: PropTypes.string,
          productFamily: PropTypes.shape({
            code: PropTypes.string
          }),
          currency: PropTypes.string,
          loyaltyPoints: PropTypes.number,
          price: PropTypes.number
        })
      )
    })
  ),
  compensationItems: compensationsShape,
  reference: PropTypes.string,
  onSubmit: PropTypes.func,
  payments: paymentsShape.isRequired,
  loading: PropTypes.bool,
  itemRefs: PropTypes.arrayOf(PropTypes.string),
  passengers: passengersShape,
  customer: customerShape,
  additionalDetails: PropTypes.arrayOf(
    PropTypes.shape({
      itemRef: PropTypes.string,
      key: PropTypes.string,
      value: PropTypes.string
    })
  ),
  currency: PropTypes.string,
  legs: journeyShape,
  username: PropTypes.string.isRequired
};

export default PrrRefund;
